import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DateRangePickerWithPresets from '../../components/DateRangePickerWithPresets/DateRangePickerWithPresets'
import TextInput from '../../components/Form/Inputs/TextInput'
import { useNotification } from '../../store/redux/hooks'
import { useSaveEmployeeVacationsMutation } from '../../store/redux/reducers/vacationSlice'
import { EmployeeVacation } from '../../store/types/utils/VacationTypes'
import { createVacationErrorMessage } from '../../store/utils/createErrorMessage'
import { formatStringDate } from '../../store/utils/dateUtils'

interface VacationDialogTypes {
  isOpen: boolean
  onClose: () => void
  selected: EmployeeVacation | null
  selectedCell: string | null
}

interface FormValues {
  startDate: string
  endDate: string
  description: string
}

const AddVacationDialog = forwardRef<HTMLDivElement, VacationDialogTypes>(
  ({ isOpen, onClose, selected, selectedCell }, ref) => {
    const { t } = useTranslation()
    const innerRef = useRef<HTMLDivElement>(null)
    const { displayNotification } = useNotification()
    useImperativeHandle(ref, () => innerRef.current || document.createElement('div'))
    const { handleSubmit, control, watch, reset, setValue } = useForm<FormValues>({
      defaultValues: {
        startDate: selectedCell || '',
        endDate: selectedCell || '',
        description: '',
      },
      mode: 'onChange',
    })
    const watchStartDate = watch('startDate')
    const watchEndDate = watch('endDate')
    const isFormValid = dayjs(watchStartDate).isValid() && dayjs(watchEndDate).isValid()

    const [saveEntry] = useSaveEmployeeVacationsMutation()

    useEffect(() => {
      if (selectedCell) {
        setValue('startDate', selectedCell)
        setValue('endDate', selectedCell)
      }
    }, [selectedCell, setValue])

    const handleClose = () => {
      reset({
        startDate: '',
        endDate: '',
        description: '',
      })
      onClose()
    }

    const saveEmployeeVacation = useCallback(
      async (startDate: string, endDate: string, employeeId: string, description: string) => {
        try {
          await saveEntry({
            dto: {
              startDate,
              endDate,
              employeeId,
              description,
            },
          }).unwrap()
          displayNotification({
            open: true,
            type: 'success',
            message: t('page.vacationView.messages.vacationAddedSuccess'),
            timeout: 3000,
          })
        } catch (error) {
          displayNotification({
            open: false,
            type: 'error',
            message: createVacationErrorMessage(error),
            timeout: 5000,
          })
        }
      },
      [saveEntry, displayNotification, t]
    )

    const onSubmit = (data: FormValues) => {
      if (
        data.startDate &&
        data.endDate &&
        selected &&
        dayjs(data.startDate).isValid() &&
        dayjs(data.endDate).isValid()
      ) {
        const employeeId = selected.id.toString()
        saveEmployeeVacation(data.startDate, data.endDate, employeeId, data.description)
        handleClose()
      }
    }

    const dateRangeDisplay = watchStartDate
      ? watchStartDate === watchEndDate
        ? formatStringDate(watchStartDate)
        : `${formatStringDate(watchStartDate)} - ${formatStringDate(watchEndDate) || '...'}`
      : ''

    const maxLength = 500

    return (
      <Dialog ref={innerRef} open={isOpen} onClose={handleClose} className={isOpen ? 'vacation-dialog-open' : ''}>
        <DialogTitle>
          <Stack direction="row" spacing={10}>
            <Stack direction="column" spacing={2}>
              {selected && (
                <Typography
                  variant="h6"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wrap: 'break-space',
                  }}
                >
                  {selected.name}
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                <BeachAccessIcon />
                <Typography variant="body1">{dateRangeDisplay}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack direction="column" spacing={2} sx={{ marginTop: 2 }}>
            <Controller
              name={'startDate'}
              control={control}
              rules={{
                required: t('page.vacationView.form.startDateRequired'),
                validate: {
                  valid: date => {
                    if (!date) return true
                    return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
                  },
                },
              }}
              render={({
                field: { onChange: onStartChange, value: startValue },
                fieldState: { error: startError },
              }) => (
                <Controller
                  name={'endDate'}
                  control={control}
                  rules={{
                    required: t('page.vacationView.form.endDateRequired'),
                    validate: {
                      valid: date => {
                        if (date === null || date === undefined) return true
                        return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
                      },
                      endDateAfterStartDate: value => {
                        const dayjsValue = dayjs(value)
                        if (dayjs.isDayjs(dayjsValue)) {
                          const startDate = watch('startDate')
                          if (dayjsValue.isBefore(startDate)) {
                            return t('page.vacationView.form.endDateBefore')
                          }
                        }
                        return true
                      },
                    },
                  }}
                  render={({ field: { onChange: onEndChange, value: endValue }, fieldState: { error: endError } }) => (
                    <Box>
                      <DateRangePickerWithPresets
                        startDate={startValue ? dayjs(startValue) : null}
                        endDate={endValue ? dayjs(endValue) : null}
                        setStartDate={newDate => onStartChange(newDate ? newDate.format('YYYY-MM-DD') : '')}
                        setEndDate={newDate => onEndChange(newDate ? newDate.format('YYYY-MM-DD') : '')}
                        startDateLabel={`${t('page.vacationView.messages.start')} *`}
                        endDateLabel={`${t('page.vacationView.messages.end')} *`}
                        presetsType={'future'}
                        selectedDate={selectedCell}
                      />
                      {startError && (
                        <Typography variant="caption" color="error">
                          {startError.message}
                        </Typography>
                      )}
                      {endError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {endError.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              )}
            />

            <TextInput
              name="description"
              control={control}
              label={t('page.vacationView.messages.desc')}
              placeholder={t('page.vacationView.enterDescription')}
              maxLength={maxLength}
              rules={{
                maxLength: {
                  value: maxLength,
                  message: t('page.worklog.form.descriptionLengthError'),
                },
              }}
              multiline
              rows={4}
              showCharCounter
            />

            <Stack direction="row" spacing={2} justifyContent="right">
              <Button variant="text" color="primary" onClick={handleClose}>
                <CloseIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
                {t('page.vacationView.cancel')}
              </Button>
              <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary" disabled={!isFormValid}>
                <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
                {t('page.vacationView.save')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    )
  }
)

export default AddVacationDialog
